import { clsx } from "clsx";
import pluralize from "pluralize";
import { useEffect, useMemo, useState } from "react";
import { Disclosure, DisclosurePanel } from "react-aria-components";
import { HStack, Icons, ItemTile, SymbolIcon, TriggerButton, TriggerButtonSize, VStack } from "widgets";

import type { CoordinateList } from "@/graphql.ts";
import {
  useFetchCoordinateListTileLazyQuery,
  useGetOrganismsQuery,
} from "@/modules/workflow/graphql/coordinate-lists.gql.ts";

import style from "./CoordinatesTile.module.css";

interface SelectedCoordinateListDisplayProps {
  selectedCoordinateList: number | null;
  selectedCoordinatesCount: number | null;
  selectedVariants: number[];
  isLoadingView?: boolean;
}

const CoordinatesTileSkeleton = ({ count }: { count?: number }) => (
  <>
    <ItemTile
      name="loading..."
      icon={<img src={Icons.Tiles.GenomeViewer} width={30} height={30} alt={undefined} />}
      id={"loading"}
      isDisabled
    />
    {/* Show count only if it is > 0. If count === 0 all coordinates are shown. If no coordinate list
        is selected, this widget will not be shown at all */}
    {!!count && (
      <HStack className={style.count}>
        <SymbolIcon icon="subdirectory_arrow_right" />
        {count} {pluralize("coordinate", count)}
      </HStack>
    )}
  </>
);

export const CoordinatesTile = ({
  selectedCoordinateList,
  selectedCoordinatesCount,
  selectedVariants,
  isLoadingView,
}: SelectedCoordinateListDisplayProps) => {
  const [listData, setListData] = useState<CoordinateList | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [loadCoordinateListTile, { loading }] = useFetchCoordinateListTileLazyQuery();

  const { data: organismsData, loading: organismsLoading } = useGetOrganismsQuery({
    variables: { orgIds: selectedVariants },
    skip: selectedVariants.length === 0,
  });

  const shouldShowSkeleton = useMemo(
    () => loading || organismsLoading || isLoadingView || (selectedCoordinateList && !listData),
    [isLoadingView, listData, loading, organismsLoading, selectedCoordinateList],
  );

  useEffect(() => {
    if (selectedCoordinateList) {
      loadCoordinateListTile({ variables: { coordinateListIds: [selectedCoordinateList] } })
        .then((data) => setListData(data.data?.coordinateLists?.[0] as CoordinateList))
        .catch((e) => {
          throw Error(e);
        });
    } else {
      setListData(null);
    }
  }, [loadCoordinateListTile, selectedCoordinateList]);

  const variantsCount = selectedVariants.length;

  if (shouldShowSkeleton) {
    return <CoordinatesTileSkeleton count={variantsCount} />;
  }

  const variants = organismsData?.organisms ?? [];

  if (!listData) {
    return (
      <ItemTile
        name="No Coordinate List"
        icon={<img src={Icons.Tiles.Entity} width={20} height={20} alt={undefined} />}
        id={"no"}
        isDisabled
      />
    );
  }

  if (listData.deleted) {
    return (
      <ItemTile
        id={listData.id}
        name="Deleted Coordinate List"
        icon={<SymbolIcon icon="delete" size={30} />}
        isDisabled
      />
    );
  }

  if (listData.hidden) {
    return (
      <ItemTile
        id={listData.id}
        name="Restricted Coordinate List"
        icon={<SymbolIcon icon="security" size={30} />}
        isDisabled
      />
    );
  }

  return (
    <Disclosure isExpanded={isExpanded} onExpandedChange={setIsExpanded}>
      <HStack>
        <ItemTile
          id={listData.id}
          name={listData.name}
          icon={<img src={Icons.Tiles.Entity} width={20} height={20} alt={undefined} />}
        />
        <TriggerButton
          slot="trigger"
          icon={isExpanded ? "unfold_less" : "unfold_more"}
          label={
            variants.length
              ? `${variants.length} variant ${pluralize("genome", variants.length)}`
              : "No variant genomes"
          }
          badge={String(variants.length)}
          badgeIntent="secondary"
          size={TriggerButtonSize.Small}
        />
      </HStack>
      <DisclosurePanel>
        <VStack className={style.panel} spacing={3}>
          <span className={style.subtitle}>
            {variants.length ? `Variant ${pluralize("genome", variants.length)}` : "No variant genomes"}
          </span>
          {variants.map((genome) =>
            genome.hidden ? (
              <ItemTile
                key={genome.id}
                name="Restricted Genome"
                icon={<SymbolIcon icon="security" size={20} />}
                id={genome.id}
                isDisabled
              />
            ) : (
              <ItemTile
                key={genome.id}
                name={genome.name ?? "Unknown Genome"}
                icon={<img src={Icons.Tiles.Entity} width={20} height={20} alt={undefined} />}
                id={genome.id}
              />
            ),
          )}
        </VStack>
      </DisclosurePanel>
      {/* Show count only if it is > 0. If count === 0 all coordinates are shown. If no coordinate list
        is selected, this widget will not be shown at all */}
      {!!selectedCoordinatesCount && (
        <HStack className={clsx(style.count, { [style.count_open]: isExpanded && !!variants.length })}>
          <SymbolIcon icon="subdirectory_arrow_right" />
          {selectedCoordinatesCount ?? 0} {pluralize("coordinate", selectedCoordinatesCount ?? 0)}
        </HStack>
      )}
    </Disclosure>
  );
};
